import {
  chainOrigins,
  clearDatesBelow,
  dateFloor,
  firstPendingField,
  legHeatCovers,
  legHeatFor,
  legHeatKey,
  legPatchWithSwap,
  nextFocus,
  reassignEditingLeg,
} from './leg_state.js';
import type { PlannerHeatRequest, PlannerHeatView } from './heat_view.js';
import type { PlannerLeg } from './planner_models.js';
import type { PlannerGuide } from './guided_fill.js';
import { useMultiSheet, windowMonths } from './sheet_months.js';
import { useEffect, useRef, useState } from 'react';
import { useStableCallback } from '../../shared/use_stable_callback.js';
import { nextLegKey, type PlannerState, type LegPager } from './planner_state.js';
import type { PlannerFormProps } from './planner_form_model.js';

/*
 * O modo MULTI do planner (várias cidades): os trechos, o heat por perna e o
 * eco da busca multi. Saiu de `planner_form.tsx` pelo teto de linhas dele.
 */

/**
 * Meses PEDIDOS ao provider por leitura de heat — o teto do fio
 * (`flight_planner_schema`, `count.max(3)`), e de propósito maior que o visível.
 *
 * Mostrar dois e buscar três deixa um mês pré-carregado fora da tela: o primeiro
 * clique no pager anda LOCALMENTE, sem gastar leitura, e no multi o `alreadyCovers`
 * curto-circuita a página seguinte. Igualar os dois faria a redução de 3 → 2 meses
 * comprar uma leitura a mais por página — e o heat já esgota crédito do SearchApi
 * sozinho.
 */
const MONTHS_FETCHED = 3;

/** Quanto dura o brilho da origem HERDADA no multi (#2136) — uma passada do
 *  feixe + shimmer (`.fpl-inherit-glow`); ver `useMultiPlanner`. É o
 *  `BRILHO_DA_ORIGEM_MS` do desenho. */
const INHERIT_GLOW_MS = 2200;

/**
 * Onde a janela visível de uma perna COMEÇA: o mês pedido, nunca antes do mês do piso
 * encadeado — antes dele não existe voo possível pra esta perna, e um read começando
 * abaixo do piso gastaria provider em meses que a grade nem abre.
 *
 * Uma função só porque a grade PINTA esta janela e o card PEDE o heat dela: derivada
 * duas vezes, o pedido cobriria meses diferentes dos exibidos — que é exatamente o
 * defeito em que os meses fora da janela buscada ficavam neutros pra sempre.
 *
 * O pager guarda o RESULTADO disto e soma seus passos sobre ele, nunca sobre um delta
 * cru: com delta, um piso que sobe depois de a pessoa ter voltado meses fazia o clamp
 * engolir os primeiros cliques adiante (zona morta do tamanho do que ela tinha voltado).
 */
function legWindowStart(fromMonth: string, floorIso: string): string {
  const floorMonth = floorIso.slice(0, 7);
  return fromMonth < floorMonth ? floorMonth : fromMonth;
}

/**
 * Vale pedir o heat da perna aberta? Devolve a CHAVE a pedir, ou `null`.
 *
 * O custo de provider é UM request por `(rota, janela visível)` — o pager inclusive,
 * porque cada navegação que sai da cobertura é uma janela que ninguém buscou. É barato
 * (um calendário cobre o range inteiro da perna de uma vez), e a alternativa era a
 * grade neutra: cada janela mora na própria entrada do `routeHeat`, então quem não pede
 * a janela nova não pinta mês nenhum fora das que já buscou.
 *
 * Por isso o guard é a COBERTURA (`alreadyCovers`), não "esta perna tem heat", e o
 * dedupe é do ÚLTIMO pedido (`lastAsked`), não de um histórico permanente: voltar a uma
 * janela cuja resposta nunca chegou (provider vazio, read falho) tem de poder pedir de
 * novo — a que chegou fica guardada e a cobertura a reconhece. O que `lastAsked`
 * garante é o que importa — despachou a chave K, o próximo render com a MESMA chave não
 * despacha (nem N por render, nem loop quando o provider devolve vazio). Esse dedupe
 * mora no despacho (`askLegHeat`), onde a ref é lida — aqui só a chave candidata.
 *
 * Pura e fora do componente porque o card já está no teto de complexidade, e a regra
 * que segura custo de provider tem de ser testável sem montar card.
 */
function shouldAskLegHeat(p: {
  isMulti: boolean;
  key: string | null;
  index: number;
  blocked: boolean;
  alreadyCovers: boolean;
}): string | null {
  if (!p.isMulti || !p.key || p.index < 0) return null;
  return p.blocked || p.alreadyCovers ? null : p.key;
}

function multiLegContext(
  state: PlannerState,
  legPager: LegPager,
  monthsVisible: number,
  todayIso: string,
) {
  const editingLegIndex = state.legIds.indexOf(state.editingLegId ?? '');
  const editingLeg = editingLegIndex >= 0 ? state.legs[editingLegIndex] : undefined;
  const legFloor =
    editingLegIndex > 0 ? dateFloor(state.legs, editingLegIndex, todayIso) : todayIso;
  const legAnchor = editingLeg?.date && editingLeg.date > legFloor ? editingLeg.date : legFloor;
  const legPaged = legPager.legId === state.editingLegId ? legPager.fromMonth : null;
  const legWindowFrom = legWindowStart(legPaged ?? legAnchor.slice(0, 7), legFloor);
  const legMonths = windowMonths({ fromMonth: legWindowFrom, count: monthsVisible });
  const queuedLegId =
    state.legIds.find(
      (id, index) =>
        id !== state.editingLegId &&
        state.legs[index] &&
        firstPendingField(state.legs[index]) != null,
    ) ?? null;
  return { editingLegIndex, editingLeg, legFloor, legWindowFrom, legMonths, queuedLegId };
}

/** Monta e emite a busca multi-trecho. Pura (fora do hook) pelo teto de
 *  `max-lines-per-function` do `useMultiPlanner`. */
function submitMultiLegs(
  legs: readonly PlannerLeg[],
  onSubmit: PlannerFormProps['onSubmit'],
  echoFields: () => Omit<PlannerHeatRequest, 'origin' | 'destination' | 'fromMonth' | 'count'>,
): void {
  // Backstop do CC-5: um trecho com origem==destino nunca embarca — o gate visual
  // (legsValid) já trava o CTA; isto protege chamadas programáticas.
  if (legs.some((leg) => leg.origin.trim() && leg.origin.trim() === leg.destination.trim())) {
    return;
  }
  const ready = legs.flatMap((leg) =>
    leg.origin.trim() && leg.destination.trim() && leg.date
      ? [{ origin: leg.origin.trim(), destination: leg.destination.trim(), date: leg.date }]
      : [],
  );
  if (ready.length) {
    onSubmit({ action: 'search_multi', tripType: 'multi_leg', legs: ready, ...echoFields() });
  }
}

/** O pedido de heat da perna em edição. Puro (fora do hook) pelo mesmo teto de
 *  `max-lines-per-function` do `useMultiPlanner`. */
function legHeatRequest(
  leg: PlannerLeg,
  legIndex: number,
  fromMonth: string,
  echoFields: () => Omit<PlannerHeatRequest, 'origin' | 'destination' | 'fromMonth' | 'count'>,
): Parameters<PlannerFormProps['fetchHeat']>[0] {
  return {
    legIndex,
    origin: leg.origin.trim(),
    destination: leg.destination.trim(),
    fromMonth,
    count: MONTHS_FETCHED,
    ...echoFields(),
  };
}

/**
 * A perna cuja origem o sistema ACABOU de herdar (#2136): ganha o brilho de
 * chegada por `INHERIT_GLOW_MS` e ele some sozinho — preenchimento instantâneo
 * passa despercebido, e a pessoa não entende de onde a origem veio. Herdar de
 * novo reinicia o relógio; o timer não precisa de cleanup porque o clear é
 * guardado pelo id (um glow novo já trocou o alvo).
 */
function useInheritGlow() {
  const [glowLegId, setGlowLegId] = useState<string | null>(null);
  const glowInherited = (before: PlannerLeg[], after: PlannerLeg[], ids: string[]) => {
    const inherited = after.findIndex((leg, i) => leg.origin.trim() && !before[i]?.origin.trim());
    const id = inherited >= 0 ? ids[inherited] : undefined;
    if (!id) return;
    setGlowLegId(id);
    setTimeout(() => {
      setGlowLegId((current) => (current === id ? null : current));
    }, INHERIT_GLOW_MS);
  };
  return { glowLegId, glowInherited };
}

// Extraído do parâmetro de `useMultiPlanner` só para caber no teto de linhas da
// função (max-lines-per-function) — sem mudança de comportamento.
type UseMultiPlannerArgs = {
  state: PlannerState;
  heat: PlannerHeatView;
  isMulti: boolean;
  readOnly: boolean;
  heatLoading: boolean;
  autoFetchHeat: boolean;
  fetchHeat: PlannerFormProps['fetchHeat'];
  onSubmit: PlannerFormProps['onSubmit'];
  echoFields: () => Omit<PlannerHeatRequest, 'origin' | 'destination' | 'fromMonth' | 'count'>;
  rebaseDraft: () => void;
  legPager: LegPager;
  setLegPager: (value: LegPager) => void;
  monthsVisible: number;
  todayIso: string;
  /** O preenchimento guiado (`useGuidedFill`) — inerte fora da página. */
  guide: PlannerGuide;
};

export function useMultiPlanner({
  state,
  heat,
  isMulti,
  readOnly,
  heatLoading,
  autoFetchHeat,
  fetchHeat,
  onSubmit,
  echoFields,
  rebaseDraft,
  legPager,
  setLegPager,
  monthsVisible,
  todayIso,
  guide,
}: UseMultiPlannerArgs) {
  const {
    legs,
    setLegs,
    legsTouched: legsTouchedRef,
    legIds,
    setLegIds,
    editingLegId,
    setEditingLegId,
    setLegFocus,
    setDateOpenLegId,
  } = state;
  const submitMulti = () => {
    rebaseDraft();
    submitMultiLegs(legs, onSubmit, echoFields);
  };
  // Proveniência do PRÓXIMO fetchHeat do effect único do multi (abaixo) —
  // alimentado tanto pela semeadura quanto por gestos (changeLeg/pageLegMonths/
  // onEdit/removeLeg). Cada gesto grava 'user' ANTES de mudar o estado; o
  // effect consome e RESETA pro baseline 'auto', senão um gesto isolado
  // contaminaria um disparo automático futuro sem relação com ele.
  const lastHeatTriggerRef = useRef<'user' | 'auto'>('auto');
  const { glowLegId, glowInherited } = useInheritGlow();
  const changeLeg = (index: number, patch: Partial<PlannerLeg>) => {
    lastHeatTriggerRef.current = 'user';
    // Escolher numa ponta a praça que já está na outra do MESMO trecho é dizer
    // "troquei as pontas" (#2197) — a mesma regra da fileira binária, e o patch
    // já sai com as duas pontas para o merge abaixo aplicar de uma vez.
    const current = legs[index];
    const applied = current ? legPatchWithSwap(current, patch) : patch;
    const patched = legs.map((leg, legIndex) =>
      legIndex === index ? { ...leg, ...applied } : leg,
    );
    const next = chainOrigins(applied.date ? clearDatesBelow(patched, index) : patched);
    legsTouchedRef.current = true;
    setLegs(next);
    glowInherited(legs, next, legIds);
    const focus = nextFocus(next, legIds, index);
    setEditingLegId(focus.id);
    setLegFocus(focus.field);
    // Toda edição FECHA o popover de data: o dia escolhido grava e fecha, e uma
    // praça editada por teclado com o popover de OUTRA perna aberto não pode
    // deixar o calendário (sempre o da perna em edição) apontando pra errada.
    setDateOpenLegId(null);
    // O guia da página reabre o da perna da vez — ou o campo dela. A inversão
    // (`legPatchWithSwap` devolveu outro objeto) só move o anel.
    guide.afterLegChange(applied !== patch, focus);
  };
  const addLeg = () => {
    const id = nextLegKey();
    const grown = chainOrigins([...legs, { origin: '', destination: '' }]);
    const ids = [...legIds, id];
    legsTouchedRef.current = true;
    setLegs(grown);
    glowInherited([...legs, { origin: '', destination: '' }], grown, ids);
    setLegIds(ids);
    setEditingLegId(id);
    const added = grown.at(-1);
    setLegFocus(added ? firstPendingField(added) : null);
    setDateOpenLegId(null);
  };
  const removeLeg = (index: number) => {
    if (legs.length <= 1) return;
    lastHeatTriggerRef.current = 'user';
    const kept = chainOrigins(legs.filter((_, legIndex) => legIndex !== index));
    const keptIds = legIds.filter((_, legIndex) => legIndex !== index);
    legsTouchedRef.current = true;
    setLegs(kept);
    setLegIds(keptIds);
    if (legIds[index] !== editingLegId) return;
    const { editingLegId: target, focus } = reassignEditingLeg(kept, keptIds);
    setEditingLegId(target);
    setLegFocus(focus);
    setDateOpenLegId(null);
  };
  /** Marca o PRÓXIMO heat como `'user'` — chamada pelo `onEdit` do
   *  `MultiLegBuilder` (abrir outra perna), que mora fora deste hook. */
  const noteHeatGestureUser = () => {
    lastHeatTriggerRef.current = 'user';
  };
  const { editingLegIndex, editingLeg, legFloor, legWindowFrom, legMonths, queuedLegId } =
    multiLegContext(state, legPager, monthsVisible, todayIso);
  /** A seta do calendário da perna pediu `month`: re-ancora a janela dela
   *  (travada no piso) — e cada passo que sai da cobertura do heat CUSTA um
   *  request (ver `shouldAskLegHeat`). Adiante NÃO tem teto de propósito: um
   *  teto de N meses tornaria inalcançável uma viagem legítima além dele. */
  const pageLegMonths = (month: string) => {
    lastHeatTriggerRef.current = 'user';
    setLegPager({ legId: editingLegId, fromMonth: legWindowStart(month, legFloor) });
  };
  const lastAsked = useRef<string | null>(null);
  const wanted = editingLeg ? legHeatKey(editingLeg, legWindowFrom) : null;
  const ask = shouldAskLegHeat({
    isMulti,
    key: wanted,
    index: editingLegIndex,
    // Perna degenerada (origem == destino) conta como bloqueada: o aviso inline
    // do editor já pede a correção; heat dela não vai ao provider (CC-5).
    blocked:
      readOnly ||
      heatLoading ||
      !autoFetchHeat ||
      (editingLeg != null && editingLeg.origin.trim() === editingLeg.destination.trim()),
    alreadyCovers: legHeatCovers(legHeatFor(heat, editingLeg, legWindowFrom), legMonths),
  });
  // Só o `ask` arma o pedido; a perna, a janela e o eco são lidos os de agora.
  const askLegHeat = useStableCallback((key: string) => {
    if (!editingLeg || key === lastAsked.current) return;
    lastAsked.current = key;
    const trigger = lastHeatTriggerRef.current;
    lastHeatTriggerRef.current = 'auto'; // reseta pro baseline após consumir
    fetchHeat(legHeatRequest(editingLeg, editingLegIndex, legWindowFrom, echoFields), { trigger });
  });
  useEffect(() => {
    if (ask) askLegHeat(ask);
  }, [ask, askLegHeat]);
  const legHeat = legHeatFor(heat, editingLeg, legWindowFrom);
  // A FOLHA (#3446): coluna infinita a partir do piso da perna (ver `sheet_months`).
  const sheet = useMultiSheet({
    heat,
    leg: editingLeg,
    loading: heatLoading,
    canFetch: !readOnly && !heatLoading,
    rewindow: (month) => {
      lastHeatTriggerRef.current = 'user';
      setLegPager({ legId: editingLegId, fromMonth: legWindowStart(month, legFloor) });
    },
  });
  return {
    sheet,
    submitMulti,
    changeLeg,
    addLeg,
    removeLeg,
    noteHeatGestureUser,
    glowLegId,
    editingLegIndex,
    editingLeg,
    legFloor,
    legWindowFrom,
    pageLegMonths,
    queuedLegId,
    legTiers: new Map((legHeat?.days ?? []).map((day): [string, number] => [day.date, day.tier])),
    legBestDate: legHeat?.cheapestDate,
    legNumber: editingLegIndex + 1,
  };
}

export type MultiPlanner = ReturnType<typeof useMultiPlanner>;
